import { height } from "@netapp/bxp-design-system-react";
import classNames from "classnames";
import { forwardRef, memo, useCallback, useMemo, useState } from "react";
import { useFeatureFlag } from "../../../../../../utils/useFeatureFlag";
import { UnmountClosed } from 'react-collapse';
import { useTranslation } from "react-i18next";
import InlineSVG from "react-inlinesvg";
import { leftNav as i18nNamespace } from "../../../../../../i18n/namespace";
import leftNavStyles from "../CategoriesNav.module.scss";
import { DATA_ATTRIBUTES } from "../constants/navigationConstants";
import { useFocusManagement, useKeyboardNavigation } from "../hooks";
import CategoryFlyoutMenu from "./CategoryFlyoutMenu";

const Category = memo(forwardRef(({ category, isOpen, isActive, open, onCategoryMenuItemClicked, selectedService, index, onKeyDown, className }, ref) => {
    const { t } = useTranslation(i18nNamespace);
    const [isAnimationFinished, setAnimationFinished] = useState(false);
    const { isFocused, onFocus, onBlur } = useFocusManagement();
    const categoryBtnKeyDownHandler = useKeyboardNavigation({
        index,
        onKeyDown,
        open,
        isOpen
    });

    const handleBlur = useCallback((e) => {
        const relatedTarget = e.relatedTarget;
        
        const isServiceCard = relatedTarget?.closest(DATA_ATTRIBUTES.SERVICE_CARD);
        const isCategoryButton = relatedTarget === ref?.current;
        const isWithinCategory = ref?.current?.contains(relatedTarget);
        
        if (!isServiceCard && !isCategoryButton && !isWithinCategory) {
            onBlur(e);
        }
    }, [onBlur, ref]);

    const categoryClassName = useMemo(() =>
        classNames(leftNavStyles.category, {
            [leftNavStyles['category-open']]: isOpen,
            [leftNavStyles['category-active']]: isActive,
        }, className), [isOpen, isActive, className]
    );

    // Filter services under Storage based on Fleet Management feature flag
    const isFleetManagementEnabled = useFeatureFlag("NetApp.FleetManagement/*");
    const filteredServices = useMemo(() => {
        const services = category?.services || [];
        if (isFleetManagementEnabled && category?.label === "Storage") {
            // Hide Management (Platform.Canvas) when FleetManagement is enabled.
            return services.filter(service => service?.key !== "Platform.Canvas");
        }
        return services;
    }, [isFleetManagementEnabled, category]);

    return <>
        <button
            onClick={open}
            onKeyDown={categoryBtnKeyDownHandler}
            onFocus={onFocus}
            onBlur={handleBlur}
            ref={ref}
            className={categoryClassName}
            data-nav-category={category.label}
            aria-label={t(category.label)}
            tabIndex={0}
        >
            {category.icon && <div className={leftNavStyles.icon}>
                {<InlineSVG src={category.icon} />}
            </div>}
            <div className={leftNavStyles['category-label']}>
                {t(category.label)}
            </div>
        </button>
        <UnmountClosed 
            onWork={({ isFullyOpened }) => setAnimationFinished(isFullyOpened)} 
            onRest={({ isFullyOpened }) => setAnimationFinished(isFullyOpened)} 
            isOpened={isOpen} 
            theme={{ collapse: height }}
        >
            <CategoryFlyoutMenu
                isAnimationFinished={isAnimationFinished}
                services={filteredServices}
                onCategoryMenuItemClicked={onCategoryMenuItemClicked}
                selectedService={selectedService}
                categoryRef={ref}
                isOpen={isOpen}
                shouldRefocus={isFocused}
            />
        </UnmountClosed>
    </>
}));

Category.displayName = 'Category';

export default Category;
